import type { NavigationGuard } from 'vue-router';

import { useRemoteConfigStore } from '@/shared/firebase/stores/remoteConfig.store';

/**
 * Timeout máximo que el guard espera a `remoteConfigStore.initialize()` antes
 * de dejar seguir la navegación. Debe quedar muy por debajo del
 * `REMOTE_CONFIG_TIMEOUT` (60s) que usa Firebase para su fetch: un cuelgue
 * de red no puede dejar al usuario 60s en pantalla vacía. Al vencer, el
 * lookup posterior de los flags cae a sus defaults y las views del
 * onboarding se renderizan con esos valores.
 */
const REMOTE_CONFIG_INIT_TIMEOUT_MS = 3000;

/**
 * Guard de la ruta `onboarding-index` que garantiza que
 * `remoteConfigStore.initialize()` corrió antes de resolver el componente.
 * Sin este guard, entrar directo a `/onboarding` (deeplink o reload) puede
 * resolver los feature flags del onboarding (SII credentials, RL edit
 * required, etc.) con sus defaults mientras Remote Config sigue fetcheando
 * en background, y mostrar un flujo distinto al configurado para el usuario.
 *
 * `initialize()` es idempotente (guard `isInitialized` + `initializationPromise`
 * compartida en el store): la primera llamada awaits el fetch, las siguientes
 * retornan inmediatamente. `Promise.race` con `REMOTE_CONFIG_INIT_TIMEOUT_MS`
 * evita que un cuelgue de red mantenga la navegación bloqueada — si vence
 * el timeout, la nav continúa y los lookups caen a los defaults. El `.catch()`
 * es defensa adicional: hoy `initialize()` captura sus errores internamente,
 * pero cualquier throw sincrónico o cambio futuro del store no debe romper
 * la navegación.
 */
export const onboardingStrategyReadyGuard: NavigationGuard = async () => {
  const remoteConfigStore = useRemoteConfigStore();

  await Promise.race([
    remoteConfigStore.initialize().catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, REMOTE_CONFIG_INIT_TIMEOUT_MS)),
  ]);
};
